/**
 * Entry validation and cleanup utilities
 *
 * These functions ensure data integrity for tracker entries,
 * particularly around the one-to-one relationship between
 * user contributions and their top-ups.
 */

import type { ManualEntry, ContributionEntry } from '../types/tracking';

/**
 * Validates that all top-ups have valid relatedEntryId references
 * pointing to existing user contributions.
 *
 * @returns Array of validation errors (empty if valid)
 */
export function validateTopupReferences(entries: ManualEntry[]): string[] {
  const errors: string[] = [];

  // Build set of valid user contribution IDs
  const userContributionIds = new Set<string>();
  entries.forEach(entry => {
    if (entry.kind === 'contribution' && entry.source === 'user') {
      userContributionIds.add(entry.id);
    }
  });

  // Check each top-up has a valid reference
  entries.forEach((entry, index) => {
    if (
      entry.kind === 'contribution' &&
      (entry.source === 'hmrc_topup' || entry.source === 'gift_aid_topup')
    ) {
      if (!entry.relatedEntryId) {
        errors.push(
          `Top-up at index ${index} (${entry.subtype}, £${entry.amount}) is missing relatedEntryId`
        );
      } else if (!userContributionIds.has(entry.relatedEntryId)) {
        errors.push(
          `Top-up at index ${index} (${entry.subtype}, £${entry.amount}) references non-existent contribution ${entry.relatedEntryId}`
        );
      }
    }
  });

  return errors;
}

/**
 * Checks if a contribution already has a top-up of the specified type.
 *
 * @param entries All tracker entries
 * @param contributionId UUID of the user contribution
 * @param topupSource 'hmrc_topup' or 'gift_aid_topup'
 * @returns The existing top-up entry, or null if none exists
 */
export function findExistingTopup(
  entries: ManualEntry[],
  contributionId: string,
  topupSource: 'hmrc_topup' | 'gift_aid_topup'
): ContributionEntry | null {
  for (const entry of entries) {
    if (
      entry.kind === 'contribution' &&
      entry.source === topupSource &&
      entry.relatedEntryId === contributionId
    ) {
      return entry;
    }
  }
  return null;
}

/**
 * Finds all top-ups (hmrc_topup or gift_aid_topup) for a given contribution.
 *
 * Used to detect duplicates and for cascade deletion.
 *
 * @param entries All tracker entries
 * @param contributionId UUID of the user contribution
 * @returns Array of top-up entries (should be max 2: one HMRC, one Gift Aid)
 */
export function findAllTopupsForContribution(
  entries: ManualEntry[],
  contributionId: string
): ContributionEntry[] {
  return entries.filter(
    (entry): entry is ContributionEntry =>
      entry.kind === 'contribution' &&
      (entry.source === 'hmrc_topup' || entry.source === 'gift_aid_topup') &&
      entry.relatedEntryId === contributionId
  );
}

/**
 * Detects duplicate top-ups (multiple top-ups of the same type for one contribution).
 *
 * @returns Map of contributionId -> array of duplicate top-ups
 */
export function detectDuplicateTopups(
  entries: ManualEntry[]
): Map<string, ContributionEntry[]> {
  const duplicates = new Map<string, ContributionEntry[]>();

  // Group top-ups by (relatedEntryId, source)
  const topupsByKey = new Map<string, ContributionEntry[]>();

  entries.forEach(entry => {
    if (
      entry.kind === 'contribution' &&
      (entry.source === 'hmrc_topup' || entry.source === 'gift_aid_topup') &&
      entry.relatedEntryId
    ) {
      const key = `${entry.relatedEntryId}:${entry.source}`;
      if (!topupsByKey.has(key)) {
        topupsByKey.set(key, []);
      }
      topupsByKey.get(key)!.push(entry);
    }
  });

  // Find keys with more than one top-up
  topupsByKey.forEach((topups, key) => {
    if (topups.length > 1) {
      const [contributionId] = key.split(':');
      duplicates.set(contributionId, topups);
    }
  });

  return duplicates;
}

/**
 * Validates entries before saving to the database.
 * Throws an error if validation fails.
 *
 * @param entries All tracker entries
 * @throws Error if validation fails
 */
export function validateEntriesBeforeSave(entries: ManualEntry[]): void {
  // Check for orphaned top-ups
  const validationErrors = validateTopupReferences(entries);
  if (validationErrors.length > 0) {
    throw new Error(
      `Cannot save: Invalid top-up references found:\n${validationErrors.join('\n')}`
    );
  }

  // Check for duplicate top-ups
  const duplicates = detectDuplicateTopups(entries);
  if (duplicates.size > 0) {
    const details = Array.from(duplicates.entries())
      .map(([id, topups]) => `  - Contribution ${id}: ${topups.length} top-ups`)
      .join('\n');
    throw new Error(
      `Cannot save: Duplicate top-ups detected:\n${details}\n\nPlease refresh the page to see the latest data.`
    );
  }

  // All entries must have IDs
  entries.forEach((entry, index) => {
    if (!entry.id) {
      throw new Error(`Entry at index ${index} is missing required 'id' field`);
    }
  });
}
